// Bounded candidate generation, and the check that an offered plan is walkable.
//
// The compromise builder only ever cut at the earlier of two end times. Two sets
// that finish together leave the first at its own end, which puts arrival after
// the second has finished, so the pair is called infeasible when leaving part
// way through the first would work. This enumerates cutoffs in both directions
// at a fixed step instead, which is a bounded search rather than an optimum.
//
// `validateRoute` is the independent check the solver does not perform. It
// walks the whole ordered route including travel from where the day started and
// to whatever comes next, so an option cannot be labeled feasible on the
// strength of its own two blocks.

import type { FestivalSet, Id } from './types.ts';

const MIN = 60000;

export interface RouteBlock {
  setId?: Id;
  stageId: Id;
  startTs: number;
  endTs: number;
}

export interface RouteCheck {
  feasible: boolean;
  errors: string[];
}

export function pairCandidates(
  a: FestivalSet,
  b: FestivalSet,
  walkMinutes: number,
  { minimumMinutes = 15, stepMinutes = 1 }: { minimumMinutes?: number; stepMinutes?: number } = {},
): RouteBlock[][] {
  if (
    !Number.isFinite(walkMinutes) ||
    walkMinutes < 0 ||
    walkMinutes > 120 ||
    !Number.isInteger(stepMinutes) ||
    stepMinutes < 1 ||
    !Number.isFinite(minimumMinutes) ||
    minimumMinutes <= 0
  ) {
    throw new Error('parameters');
  }
  if (
    ![a.startTs, a.endTs, b.startTs, b.endTs].every(Number.isFinite) ||
    a.endTs <= a.startTs ||
    b.endTs <= b.startTs ||
    a.endTs - a.startTs > 24 * 60 * MIN ||
    b.endTs - b.startTs > 24 * 60 * MIN
  ) {
    throw new Error('set_time');
  }

  const out: RouteBlock[][] = [];
  for (const [first, second] of [
    [a, b],
    [b, a],
  ]) {
    const latest = Math.min(first.endTs, second.endTs - (walkMinutes + minimumMinutes) * MIN);
    for (let leave = first.startTs + minimumMinutes * MIN; leave <= latest; leave += stepMinutes * MIN) {
      const arrive = Math.max(second.startTs, leave + walkMinutes * MIN);
      out.push([
        { setId: first.id, stageId: first.stageId, startTs: first.startTs, endTs: leave },
        { setId: second.id, stageId: second.stageId, startTs: arrive, endTs: second.endTs },
      ]);
    }
  }
  return out;
}

export interface RouteContext {
  sets?: FestivalSet[];
  required?: { stageId: Id; setId?: Id; startTs: number; endTs: number }[];
  walkMinutes: (from: Id, to: Id) => number;
  dayStart?: number;
  dayEnd?: number;
  startAt?: { stageId: Id; time: number } | null;
  endAt?: { stageId: Id; time: number } | null;
}

export function validateRoute(blocks: RouteBlock[], ctx: RouteContext): RouteCheck {
  const {
    sets = [],
    required = [],
    walkMinutes,
    dayStart = -Infinity,
    dayEnd = Infinity,
    startAt = null,
    endAt = null,
  } = ctx;

  const errors: string[] = [];
  const sorted = [...blocks].sort((x, y) => x.startTs - y.startTs);
  const catalog = new Map(sets.map((s) => [s.id, s]));

  for (const b of sorted) {
    if (
      !Number.isFinite(b.startTs) ||
      !Number.isFinite(b.endTs) ||
      b.endTs <= b.startTs ||
      b.startTs < dayStart ||
      b.endTs > dayEnd ||
      !b.stageId
    ) {
      errors.push('invalid_interval');
    }
    if (b.setId) {
      const s = catalog.get(b.setId);
      if (!s || s.stageId !== b.stageId || b.startTs < s.startTs || b.endTs > s.endTs) {
        errors.push('outside_set');
      }
    }
  }

  const route: RouteBlock[] = [
    ...(startAt ? [{ stageId: startAt.stageId, startTs: startAt.time, endTs: startAt.time }] : []),
    ...sorted,
    ...(endAt ? [{ stageId: endAt.stageId, startTs: endAt.time, endTs: endAt.time }] : []),
  ];

  for (let i = 1; i < route.length; i++) {
    const prev = route[i - 1];
    const next = route[i];
    const walk = prev.stageId === next.stageId ? 0 : walkMinutes(prev.stageId, next.stageId);
    if (!Number.isFinite(walk) || walk < 0) errors.push('unknown_walk');
    else if (prev.endTs + walk * MIN > next.startTs) errors.push('unreachable');
  }

  for (const r of required) {
    const covered = sorted.some(
      (b) =>
        b.stageId === r.stageId &&
        (!r.setId || r.setId === b.setId) &&
        b.startTs <= r.startTs &&
        b.endTs >= r.endTs,
    );
    if (!covered) errors.push('required_block_missing');
  }

  return { feasible: errors.length === 0, errors: [...new Set(errors)] };
}

export interface Rendezvous {
  placeId: Id;
  label: string;
  startTs: number;
  endTs: number;
}

// A meeting is added to each member's route and the whole route rechecked. No
// set is truncated to make room: a revised route is a separate proposal, and
// silently cutting somebody's set short is how a plan gets accepted that nobody
// agreed to.
export function validateRendezvous({
  meeting,
  members,
  plans,
  walkMinutes,
}: {
  meeting: Rendezvous;
  members: Id[];
  plans: Record<Id, { blocks: RouteBlock[] } & Partial<RouteContext>>;
  walkMinutes: (from: Id, to: Id) => number;
}): { feasible: boolean; reason?: string; results?: ({ id: Id } & Partial<RouteCheck>)[] } {
  if (
    !meeting.placeId ||
    !meeting.label ||
    !Number.isFinite(meeting.startTs) ||
    !Number.isFinite(meeting.endTs) ||
    meeting.endTs <= meeting.startTs
  ) {
    return { feasible: false, reason: 'meeting_details' };
  }

  const results = members.map((id) => {
    const route = plans[id];
    if (!route) return { id, feasible: false, errors: ['missing_plan'] };
    const visit: RouteBlock = {
      stageId: meeting.placeId,
      startTs: meeting.startTs,
      endTs: meeting.endTs,
    };
    return { id, ...validateRoute([...route.blocks, visit], { ...route, walkMinutes }) };
  });

  return { feasible: members.length > 0 && results.every((r) => r.feasible), results };
}
